import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Search, AlertCircle, Ban, CheckCircle2, UserCheck, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  DataTable,
  PageHeader,
  type DataTableColumn,
} from "@/components/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useAdminUsers, useCustomerMemberProfile, useVerifyUserKycMutation } from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import type { AdminUserListDto } from "@/types/admin-user.dto";
import { CustomerKycStatus } from "@/types/customer.dto";

function displayName(user: AdminUserListDto) {
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.userName || user.email;
}

interface DetailRowProps {
  label: string;
  value?: string | null;
  mono?: boolean;
  onCopy?: (text: string) => void;
}

function DetailRow({ label, value, mono, onCopy }: DetailRowProps) {
  const text = value?.trim() ? value : "—";
  const canCopy = Boolean(value?.trim() && onCopy);
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/60 py-2.5 last:border-0">
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className={`mt-0.5 break-all text-sm text-foreground ${mono ? "font-mono" : ""}`}>{text}</p>
      </div>
      {canCopy ? (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0"
          onClick={() => onCopy?.(value!.trim())}
          aria-label={`Copy ${label}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </div>
  );
}

const AdminKycApprovals = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [kycFilter, setKycFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedUser, setSelectedUser] = useState<AdminUserListDto | null>(null);
  const [rejectingUser, setRejectingUser] = useState<AdminUserListDto | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const filter = useMemo(
    () => ({
      search: search.trim() || undefined,
      role: "customer", // KYC submissions are from customers
      page,
      pageSize,
    }),
    [search, page, pageSize],
  );

  const { data, isLoading } = useAdminUsers(filter);
  const verifyKycMutation = useVerifyUserKycMutation();

  // Load the full profile (including kycDetails object) when a user is selected
  const { data: memberProfile, isLoading: isProfileLoading } = useCustomerMemberProfile(
    selectedUser?.id ?? "",
    Boolean(selectedUser),
  );

  // Client-side filter to show different KYC statuses
  const users = useMemo(() => {
    const rawUsers = data?.items ?? [];
    if (kycFilter === "all") return rawUsers;
    if (kycFilter === "pending") {
      return rawUsers.filter((u) => u.kycStatus === CustomerKycStatus.Submitted);
    }
    if (kycFilter === "verified") {
      return rawUsers.filter((u) => u.kycStatus === CustomerKycStatus.Verified);
    }
    if (kycFilter === "rejected") {
      return rawUsers.filter((u) => u.kycStatus === CustomerKycStatus.Rejected);
    }
    if (kycFilter === "not_submitted") {
      return rawUsers.filter((u) => !u.kycStatus || u.kycStatus === CustomerKycStatus.NotSubmitted);
    }
    return rawUsers;
  }, [data?.items, kycFilter]);

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copied` });
  };

  const handleApproveKyc = async (userId: string) => {
    try {
      await verifyKycMutation.mutateAsync({ userId, approve: true });
      toast({ title: t("kyc.verified"), description: "KYC marked as verified successfully" });
      setSelectedUser(null);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleRejectKyc = async () => {
    if (!rejectingUser) return;
    if (!rejectionReason.trim()) {
      toast({ title: "Please enter a rejection reason", variant: "destructive" });
      return;
    }
    try {
      await verifyKycMutation.mutateAsync({
        userId: rejectingUser.id,
        approve: false,
        rejectionReason: rejectionReason.trim(),
      });
      toast({ title: t("kyc.rejected"), description: "KYC request rejected successfully" });
      setRejectingUser(null);
      setRejectionReason("");
      setSelectedUser(null);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const kycStatusBadge = (status?: number) => {
    switch (status) {
      case CustomerKycStatus.Submitted:
        return <Badge className="bg-warning/10 text-warning border-warning/20">{t("kyc.pending")}</Badge>;
      case CustomerKycStatus.Verified:
        return <Badge className="bg-success/10 text-success border-success/20">{t("kyc.verified")}</Badge>;
      case CustomerKycStatus.Rejected:
        return <Badge className="bg-destructive/10 text-destructive border-destructive/20">{t("kyc.rejected")}</Badge>;
      default:
        return <Badge variant="secondary">Not Submitted</Badge>;
    }
  };

  const columns: DataTableColumn<AdminUserListDto>[] = [
    {
      id: "name",
      header: t("kyc.user"),
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-semibold text-foreground">{displayName(row)}</span>
          <span className="text-xs text-muted-foreground">{row.userName}</span>
        </div>
      ),
    },
    {
      id: "email",
      header: "Email",
      cell: (row) => <span className="text-sm font-medium">{row.email}</span>,
    },
    {
      id: "phoneNumber",
      header: "Phone",
      cell: (row) => <span className="text-sm text-muted-foreground">{row.phoneNumber ?? "—"}</span>,
    },
    {
      id: "kycStatus",
      header: t("kyc.status"),
      cell: (row) => kycStatusBadge(row.kycStatus),
    },
    {
      id: "createdAt",
      header: t("kyc.submitted_at"),
      cell: (row) => <span className="text-xs text-muted-foreground">{new Date(row.createdAt).toLocaleDateString()}</span>,
    },
    {
      id: "actions",
      header: t("kyc.actions"),
      cell: (row) => (
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5"
          onClick={() => setSelectedUser(row)}
        >
          <Eye className="h-3.5 w-3.5" />
          Details
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <PageHeader
          title={t("kyc.title")}
          description={t("kyc.subtitle")}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-[320px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={t("kyc.search_placeholder")}
            className="w-full bg-background pl-9 pr-4 py-2 border rounded-md text-sm outline-none focus:ring-1 focus:ring-primary"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <Select
          value={kycFilter}
          onValueChange={(v) => {
            setKycFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-[180px]">
            <SelectValue placeholder="KYC Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("kyc.all_status")}</SelectItem>
            <SelectItem value="pending">{t("kyc.pending")}</SelectItem>
            <SelectItem value="verified">{t("kyc.verified")}</SelectItem>
            <SelectItem value="rejected">{t("kyc.rejected")}</SelectItem>
            <SelectItem value="not_submitted">Not Submitted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={users}
        getRowId={(row) => row.id}
        emptyMessage="No KYC submissions found"
        loading={isLoading}
        pagination={{
          page,
          pageSize: data?.pageSize ?? pageSize,
          totalCount: data?.totalItems ?? 0,
          totalPages: data?.totalPages ?? 1,
          from: data?.totalItems ? (page - 1) * (data?.pageSize ?? pageSize) + 1 : 0,
          to: Math.min(page * (data?.pageSize ?? pageSize), data?.totalItems ?? 0),
        }}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />

      {/* KYC Verification Drawer */}
      <Sheet open={Boolean(selectedUser)} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          {selectedUser ? (
            <>
              <SheetHeader>
                <SheetTitle>{t("kyc.details")}</SheetTitle>
                <SheetDescription>
                  Review user KYC identity and banking information
                </SheetDescription>
              </SheetHeader>

              {isProfileLoading ? (
                <div className="mt-6 space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : memberProfile ? (
                <div className="mt-6 space-y-6">
                  {/* Status Banner */}
                  <div className="p-3.5 rounded-xl border flex items-start gap-3 bg-muted/40">
                    <div className="mt-0.5">
                      {memberProfile.kycStatus === CustomerKycStatus.Verified ? (
                        <CheckCircle2 className="h-5 w-5 text-success" />
                      ) : memberProfile.kycStatus === CustomerKycStatus.Rejected ? (
                        <Ban className="h-5 w-5 text-destructive" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-warning" />
                      )}
                    </div>
                    <div>
                      <span className="font-semibold text-sm flex items-center gap-1.5 text-foreground">
                        {t("kyc.status")}: {kycStatusBadge(memberProfile.kycStatus)}
                      </span>
                      {memberProfile.kycRejectionReason && (
                        <p className="mt-1 text-xs text-destructive font-medium">
                          Reason: {memberProfile.kycRejectionReason}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Section 1: Personal Details */}
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-foreground border-b pb-1.5 mb-2">
                      {t("kyc.personal_details")}
                    </h3>
                    <DetailRow label="User ID" value={memberProfile.userId} mono onCopy={(v) => copyText(v, "User ID")} />
                    <DetailRow label={t("kyc.full_name")} value={displayName(selectedUser)} />
                    <DetailRow label="Email" value={selectedUser.email} onCopy={(v) => copyText(v, "Email")} />
                    <DetailRow label="Phone" value={selectedUser.phoneNumber} onCopy={(v) => copyText(v, "Phone")} />
                    <DetailRow label="Member Code" value={memberProfile.memberCode} mono onCopy={(v) => copyText(v, "Member Code")} />
                  </div>

                  {/* Section 2: Identity Documents */}
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-foreground border-b pb-1.5 mb-2">
                      {t("kyc.identity_details")}
                    </h3>
                    <DetailRow
                      label={t("kyc.aadhaar")}
                      value={memberProfile.kycDetails?.aadhaarNumber}
                      mono
                      onCopy={(v) => copyText(v, t("kyc.aadhaar"))}
                    />
                    <DetailRow
                      label={t("kyc.pan")}
                      value={memberProfile.kycDetails?.panNumber}
                      mono
                      onCopy={(v) => copyText(v, t("kyc.pan"))}
                    />
                  </div>

                  {/* Section 3: Bank Details */}
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-foreground border-b pb-1.5 mb-2">
                      {t("kyc.bank_details")}
                    </h3>
                    <DetailRow
                      label={t("kyc.bank_name")}
                      value={memberProfile.kycDetails?.bankName}
                    />
                    <DetailRow
                      label={t("kyc.account_name")}
                      value={memberProfile.kycDetails?.bankAccountName}
                    />
                    <DetailRow
                      label={t("kyc.account_number")}
                      value={memberProfile.kycDetails?.bankAccountNumber}
                      mono
                      onCopy={(v) => copyText(v, t("kyc.account_number"))}
                    />
                    <DetailRow
                      label={t("kyc.ifsc")}
                      value={memberProfile.kycDetails?.bankIfsc}
                      mono
                      onCopy={(v) => copyText(v, t("kyc.ifsc"))}
                    />
                  </div>

                  {/* Approval Actions */}
                  {memberProfile.kycStatus === CustomerKycStatus.Submitted && (
                    <div className="pt-4 flex gap-3">
                      <Button
                        className="flex-1 bg-success hover:bg-success/90"
                        onClick={() => handleApproveKyc(selectedUser.id)}
                        disabled={verifyKycMutation.isPending}
                      >
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        {t("kyc.approve")}
                      </Button>
                      <Button
                        variant="destructive"
                        className="flex-1"
                        onClick={() => setRejectingUser(selectedUser)}
                        disabled={verifyKycMutation.isPending}
                      >
                        <Ban className="mr-2 h-4 w-4" />
                        {t("kyc.reject")}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-8 text-center text-sm text-muted-foreground">
                  No KYC details found for this user.
                </div>
              )}
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Reject KYC Dialog */}
      <Dialog open={Boolean(rejectingUser)} onOpenChange={(open) => !open && setRejectingUser(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("kyc.reject_title")}</DialogTitle>
            <DialogDescription>
              {t("kyc.reject_description")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="reason">{t("kyc.rejection_reason")}</Label>
              <Textarea
                id="reason"
                rows={3}
                placeholder={t("kyc.reason_placeholder")}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectingUser(null)}>
              {t("cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectKyc}
              disabled={verifyKycMutation.isPending}
            >
              {t("kyc.reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminKycApprovals;
